import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatBottomSheet, MatSnackBar } from '@angular/material';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import pdfMake from 'pdfmake/build/pdfmake';
import { ConduiteService } from '../../../../core/apps/conduite.service';
import { LigneOrdonnance, lignesDe, Ordonnance } from '../../../../models/ordonnance';
import { buildDoubleOrdonnanceDocDef, buildOrdonnanceDocDef, formatDateFr, TEMPS_ATTENTE } from './ordonnance-pdf';
import { OrdonnancePartageService } from './ordonnance-partage.service';
import { pdfMakeOrdonnance } from './pdf-setup';
import {
	PartageOrdonnanceSheetComponent,
	PartageOrdonnanceSheetData,
} from './partage-ordonnance-sheet/partage-ordonnance-sheet.component';

// Valeur du <mat-select> qui déclenche la saisie d'un nouvel élément (ferme / médicament)
export const AUTRE = '__autre__';

const DEFAULT_VOIE = 'Orale (aliment médicamenteux)';

// Température de l'eau plausible pour un élevage aquacole (°C)
const TEMP_MIN = 0;
const TEMP_MAX = 45;

/** Forme de comparaison : sans accents, sans casse, espaces réduits (« Maxiflor  20% » = « MAXIFLOR 20% »). */
function norm(x: any): string {
	return String(x || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/** Renvoie l'orthographe déjà présente dans la liste si le nom saisi n'en diffère que par la casse/les accents. */
function canonique(nom: string, liste: string[]): string {
	const existant = liste.find(x => norm(x) === norm(nom));
	return existant || nom;
}

// Liste pré-établie du client, injectée une seule fois dans la collection Medicaments (modifiable ensuite
// depuis Gestion dynamique → Médicaments)
const MEDICAMENTS_INITIAUX = [
	'FLORKEM BIDON 1L TN',
	'OROFLOX 20% 1 L',
	'FLUMED 50% FLACON DE 1L',
	'MAXIFLOR 20% 1 L',
	'MEGAFLOR 20% 1L',
	'FLORVET 20 FLACON DE 1 LITRES',
	'C VIT 1L',
	'VITASOL',
	'TERACYL 50% SACHET DE 100G',
	'AVIOX 50% 500 GR',
	'VITAMINE C 1KG',
	'TETRASOLUB 50% (oxytetracycline)',
];

const VETERINAIRE_PAR_DEFAUT = {
	veterinaireNom: 'Docteur Gall Mejdi',
	veterinaireOrdre: '1351',
	veterinaireAdresse: 'Cité Ennassim, R9, Monastir 5000',
};

interface MenuContext {
	action: 'apercu' | 'pdf' | 'partager';
	ordonnance?: Ordonnance; // absent pour l'aperçu du formulaire
}

@Component({
	selector: 'kt-ordenance',
	templateUrl: './ordenance.component.html',
	styleUrls: ['./ordenance.component.scss'],
})
export class OrdenanceComponent implements OnInit, OnDestroy {
	readonly AUTRE = AUTRE;
	readonly tempsAttente = TEMPS_ATTENTE;
	readonly voies = [DEFAULT_VOIE, "Orale (eau d'élevage)", 'Bain (balnéation)', 'Injection'];

	form: FormGroup;
	tabIndex = 0;
	editingId: string = null;
	examenOrigine: { id: string; date: string; molecule: string } = null;

	fermes: string[] = [];
	private fermesFirestore: string[] = [];
	medicaments: string[] = [];
	medicamentItems: { id: string; libelle: string }[] = [];
	nouveauMedicament = '';
	filtreMedicament = '';
	medicamentEnEdition: { id: string; libelle: string } = null;
	ordonnances: Ordonnance[] = [];
	filtreFerme = '';
	filtreCage = '';
	menuContext: MenuContext;
	saving = false;

	// Sélection pour « combiner 2 ordonnances sur une page A4 » (économie de papier : 2 A5 portrait côte à côte
	// = 1 A4 paysage). Toujours 2 ordonnances DIFFÉRENTES, jamais 2 copies de la même — le vétérinaire coche
	// 2 lignes dans la liste, une 3e case reste désactivée tant que 2 sont déjà cochées.
	combinaisonSelection: Ordonnance[] = [];

	// Menu de partage personnalisé (remplace l'appel direct au partage natif) : ferme/cage/date affichés
	// dans l'en-tête, 4 actions explicites. Voir ouvrirMenuPartage().
	menuPartage: { ordonnance: Ordonnance; avecCachet: boolean; avecEntite: boolean } = null;
	// Bouton « Partager » à l'origine de la demande : sert à ancrer le menu près de lui sur ordinateur
	// (sur petit écran, le menu reste centré, plus adapté au doigt qu'un dropdown collé au bord).
	menuPartageBouton: HTMLElement = null;
	menuPartagePosition: { top: string; left: string } = null;
	private readonly LARGEUR_PANNEAU_ANCRE = 340;
	// Référence au panneau une fois affiché : sert à mesurer sa VRAIE hauteur (voir ouvrirMenuPartage) plutôt
	// que de deviner une hauteur fixe, qui provoquait un décalage important avec le bouton cliqué.
	@ViewChild('panelPartage', { static: false }) panelPartageEl?: ElementRef<HTMLElement>;

	// Ferme de l'examen d'origine si elle n'est pas (encore) dans la liste des entreprises
	private fermeExamen = '';
	private medicamentsSeeded = false;
	private destroy$ = new Subject<void>();

	constructor(
		private fb: FormBuilder,
		private conduiteService: ConduiteService,
		private route: ActivatedRoute,
		private snackBar: MatSnackBar,
		private bottomSheet: MatBottomSheet,
		private partageService: OrdonnancePartageService,
		// L'app est en OnPush : les données Firestore arrivées hors événement doivent le signaler
		private cdr: ChangeDetectorRef,
	) {}

	ngOnInit() {
		this.form = this.fb.group({
			ferme: [''],
			fermeAutre: [''],
			dateOrdonnance: [this.today()],
			numCage: [''],
			temperatureEau: [''],
			lignes: this.fb.array([this.nouvelleLigne()]),
			voieAdministration: [DEFAULT_VOIE],
			recommandations: [''],
			...VETERINAIRE_PAR_DEFAUT,
		});

		this.conduiteService.getListEntreprise().pipe(takeUntil(this.destroy$)).subscribe((actions: any[]) => {
			this.fermesFirestore = this.libelles(actions);
			this.majFermes();
			this.cdr.markForCheck();
		}, e => this.signalerErreur('Lecture des fermes impossible', e));
		this.conduiteService.getListMedicaments().pipe(takeUntil(this.destroy$)).subscribe((actions: any[]) => {
			this.medicamentItems = actions
				.map(a => ({ id: a.payload.doc.id as string, libelle: String(a.payload.doc.data().libelle || '').trim() }))
				.filter(m => !!m.libelle)
				.sort((a, b) => a.libelle.localeCompare(b.libelle, 'fr', { sensitivity: 'base' }));
			this.medicaments = this.medicamentItems.map(m => m.libelle);
			this.cdr.markForCheck();
			if (!this.medicaments.length && !this.medicamentsSeeded) {
				this.medicamentsSeeded = true;
				MEDICAMENTS_INITIAUX.forEach(m => this.conduiteService.ensureLibelleItem('Medicaments', m)
					.catch(e => this.signalerErreur('Ajout des médicaments initiaux impossible', e)));
			}
		}, e => this.signalerErreur('Lecture des médicaments impossible', e));
		this.conduiteService.getListOrdonnances().pipe(takeUntil(this.destroy$)).subscribe((actions: any[]) => {
			this.ordonnances = actions.map(a => ({ ...a.payload.doc.data(), id: a.payload.doc.id } as Ordonnance));
			this.cdr.markForCheck();
		}, e => this.signalerErreur('Lecture des ordonnances impossible', e));

		this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
			const examenId = params.get('examen');
			if (examenId) {
				this.prefillFromExamen(examenId);
			}
		});
	}

	// Les accesseurs ci-dessous doivent supporter d'être lus avant ngOnInit (ngrx parcourt les objets en dev)
	get lignes(): FormArray {
		return this.form ? this.form.get('lignes') as FormArray : null;
	}

	private nouvelleLigne(l: { medicament?: string; medicamentAutre?: string; dose?: string; duree?: string } = {}): FormGroup {
		return this.fb.group({
			medicament: [l.medicament || ''],
			medicamentAutre: [l.medicamentAutre || ''],
			dose: [l.dose || ''],
			duree: [l.duree || ''],
		});
	}

	private setLignes(lignes: { medicament?: string; medicamentAutre?: string; dose?: string; duree?: string }[]) {
		while (this.lignes.length) {
			this.lignes.removeAt(0);
		}
		(lignes.length ? lignes : [{}]).forEach(l => this.lignes.push(this.nouvelleLigne(l)));
	}

	/** Option déjà choisie dans une autre ligne de cette ordonnance (grisée dans la liste). */
	dejaChoisi(medicament: string, index: number): boolean {
		return this.lignes.controls.some((c, i) => i !== index && c.value.medicament === medicament);
	}

	ajouterLigne() {
		this.lignes.push(this.nouvelleLigne());
	}

	retirerLigne(i: number) {
		if (this.lignes.length > 1) {
			this.lignes.removeAt(i);
		}
	}

	get ordonnancesFiltrees(): Ordonnance[] {
		const ferme = (this.filtreFerme || '').trim().toLowerCase();
		const cage = (this.filtreCage || '').trim().toLowerCase();
		return (this.ordonnances || []).filter(o =>
			(!ferme || String(o.ferme).toLowerCase().indexOf(ferme) !== -1) &&
			(!cage || String(o.numCage).toLowerCase().indexOf(cage) !== -1));
	}

	formatDate(iso: string): string {
		return formatDateFr(iso);
	}

	// ── Formulaire ────────────────────────────────────────────────────────────

	effacer() {
		this.editingId = null;
		this.examenOrigine = null;
		this.fermeExamen = '';
		this.majFermes();
		this.form.reset({
			ferme: '', fermeAutre: '', dateOrdonnance: this.today(), numCage: '', temperatureEau: '',
			voieAdministration: DEFAULT_VOIE, recommandations: '', ...VETERINAIRE_PAR_DEFAUT,
		});
		this.setLignes([{}]);
	}

	/** Valeurs du formulaire → ordonnance (résout les choix « Autre… »), ou null + message si incomplet. */
	private lireFormulaire(): Ordonnance | null {
		const v = this.form.value;
		const clean = (x: any) => String(x || '').trim();
		const ferme = canonique(v.ferme === AUTRE ? clean(v.fermeAutre) : clean(v.ferme), this.fermes);

		const lignes: LigneOrdonnance[] = (v.lignes || []).map(l => ({
			medicament: canonique(l.medicament === AUTRE ? clean(l.medicamentAutre) : clean(l.medicament), this.medicaments),
			dose: clean(l.dose),
			duree: clean(l.duree),
		}));

		// Température : « 29,5 » ou « 29.5 » (suffixe °C toléré), normalisée en « 29.5 »
		const temperatureSaisie = clean(v.temperatureEau).replace(/\s*°?\s*c$/i, '').replace(',', '.');
		const temperature = /^\d+(\.\d+)?$/.test(temperatureSaisie) ? Number(temperatureSaisie) : NaN;

		const manquants: string[] = [
			[ferme, 'la ferme'], [v.dateOrdonnance, 'la date'], [clean(v.numCage), 'le numéro de cage'],
			[clean(v.temperatureEau), "la température de l'eau"], [clean(v.veterinaireNom), 'le nom du vétérinaire'],
		].filter(([valeur]) => !valeur).map(([, nom]) => nom);
		lignes.forEach((l, i) => {
			const prefixe = lignes.length > 1 ? `médicament ${i + 1} : ` : '';
			[[l.medicament, 'le médicament'], [l.dose, 'la dose'], [l.duree, 'la durée']]
				.filter(([valeur]) => !valeur)
				.forEach(([, nom]) => manquants.push(prefixe + nom));
		});

		const invalides: string[] = [];
		if (clean(v.temperatureEau) && (isNaN(temperature) || temperature < TEMP_MIN || temperature > TEMP_MAX)) {
			invalides.push(`la température de l'eau doit être un nombre entre ${TEMP_MIN} et ${TEMP_MAX} °C`);
		}
		// Un même médicament ne peut figurer qu'une fois dans l'ordonnance
		const vus: { [nom: string]: number } = {};
		lignes.filter(l => l.medicament).forEach(l => vus[norm(l.medicament)] = (vus[norm(l.medicament)] || 0) + 1);
		lignes.filter(l => l.medicament && vus[norm(l.medicament)] > 1).forEach(l => {
			const message = `« ${l.medicament} » est choisi plusieurs fois`;
			if (invalides.indexOf(message) === -1) {
				invalides.push(message);
			}
		});

		if (manquants.length || invalides.length) {
			const messages = [
				manquants.length ? `À renseigner : ${manquants.join(', ')}` : '',
				...invalides.map(m => `${m}`),
			].filter(m => !!m);
			this.snackBar.open(messages.join(' — '), 'OK', { duration: 9000 });
			return null;
		}

		const ordonnance: Ordonnance = {
			ferme,
			dateOrdonnance: v.dateOrdonnance,
			numCage: clean(v.numCage),
			temperatureEau: String(temperature),
			lignes,
			// noms joints : sert à la liste, à l'Excel et à la règle Firestore (champ obligatoire)
			medicament: lignes.map(l => l.medicament).join(' + ').slice(0, 300),
			voieAdministration: v.voieAdministration,
			recommandations: clean(v.recommandations),
			veterinaireNom: clean(v.veterinaireNom),
			veterinaireOrdre: clean(v.veterinaireOrdre),
			veterinaireAdresse: clean(v.veterinaireAdresse),
		};
		if (this.examenOrigine) {
			ordonnance.examenId = this.examenOrigine.id;
		}
		return ordonnance;
	}

	async enregistrer() {
		const ordonnance = this.lireFormulaire();
		if (!ordonnance) {
			return;
		}
		const doublon = !this.editingId && this.ordonnances.some(o =>
			norm(o.ferme) === norm(ordonnance.ferme) && o.dateOrdonnance === ordonnance.dateOrdonnance &&
			norm(o.numCage) === norm(ordonnance.numCage));
		if (doublon && !window.confirm(
			`Une ordonnance existe déjà pour ${ordonnance.ferme}, cage ${ordonnance.numCage}, le ${this.formatDate(ordonnance.dateOrdonnance)}.\n\nEnregistrer quand même ?`)) {
			return;
		}
		this.saving = true;
		try {
			// Un nom saisi via « Autre… » rejoint la liste pré-établie (ferme = référentiel Entreprises des examens)
			await this.conduiteService.ensureLibelleItem('Entreprises', ordonnance.ferme);
			for (const ligne of ordonnance.lignes) {
				await this.conduiteService.ensureLibelleItem('Medicaments', ligne.medicament);
			}
			if (this.editingId) {
				await this.conduiteService.updateOrdonnance(this.editingId, ordonnance);
			} else {
				await this.conduiteService.saveOrdonnance(ordonnance);
			}
			this.snackBar.open(this.editingId ? 'Ordonnance modifiée' : 'Ordonnance enregistrée', 'OK', { duration: 3000 });
			this.effacer();
			this.tabIndex = 1;
		} catch (e) {
			this.signalerErreur("Échec de l'enregistrement", e);
		} finally {
			this.saving = false;
			this.cdr.markForCheck();
		}
	}

	modifier(o: Ordonnance) {
		const ferme = this.fermes.indexOf(o.ferme) !== -1 ? o.ferme : AUTRE;
		this.editingId = o.id;
		this.examenOrigine = null;
		this.form.reset({
			ferme, fermeAutre: ferme === AUTRE ? o.ferme : '',
			dateOrdonnance: o.dateOrdonnance, numCage: o.numCage, temperatureEau: o.temperatureEau,
			voieAdministration: o.voieAdministration, recommandations: o.recommandations,
			veterinaireNom: o.veterinaireNom, veterinaireOrdre: o.veterinaireOrdre, veterinaireAdresse: o.veterinaireAdresse,
		});
		this.setLignes(lignesDe(o).map(l => {
			const connu = this.medicaments.indexOf(l.medicament) !== -1;
			return {
				medicament: connu ? l.medicament : AUTRE, medicamentAutre: connu ? '' : l.medicament,
				dose: l.dose, duree: l.duree,
			};
		}));
		this.tabIndex = 0;
	}

	supprimer(o: Ordonnance) {
		if (!window.confirm(`Supprimer l'ordonnance ${o.ferme} — ${this.formatDate(o.dateOrdonnance)} (${o.numCage}) ?`)) {
			return;
		}
		this.conduiteService.deleteOrdonnance(o.id).then(
			() => this.snackBar.open('Ordonnance supprimée', 'OK', { duration: 3000 }),
			() => this.snackBar.open('Échec de la suppression', 'OK', { duration: 5000 }),
		);
	}

	// ── Onglet Médicaments ────────────────────────────────────────────────────

	get medicamentsFiltres() {
		const f = (this.filtreMedicament || '').trim().toLowerCase();
		return (this.medicamentItems || []).filter(m => !f || m.libelle.toLowerCase().indexOf(f) !== -1);
	}

	ajouterMedicament() {
		const libelle = this.nouveauMedicament.trim();
		if (!libelle) {
			return;
		}
		const existant = this.medicaments.find(m => norm(m) === norm(libelle));
		if (existant) {
			this.snackBar.open(`« ${existant} » existe déjà dans la liste`, 'OK', { duration: 5000 });
			return;
		}
		this.conduiteService.ensureLibelleItem('Medicaments', libelle).then(
			() => this.nouveauMedicament = '',
			e => this.signalerErreur("Ajout du médicament impossible", e),
		);
	}

	editerMedicament(m: { id: string; libelle: string }) {
		this.medicamentEnEdition = { ...m };
	}

	validerMedicament() {
		const m = this.medicamentEnEdition;
		if (!m || !m.libelle.trim()) {
			return;
		}
		const autre = this.medicamentItems.find(x => x.id !== m.id && norm(x.libelle) === norm(m.libelle));
		if (autre) {
			this.snackBar.open(`« ${autre.libelle} » existe déjà dans la liste`, 'OK', { duration: 5000 });
			return;
		}
		this.conduiteService.updateLibelleItem('Medicaments', m.id, m.libelle).then(
			() => this.medicamentEnEdition = null,
			e => this.signalerErreur('Modification du médicament impossible', e),
		);
	}

	supprimerMedicament(m: { id: string; libelle: string }) {
		if (!window.confirm(`Supprimer « ${m.libelle} » de la liste ? Les ordonnances déjà enregistrées ne changent pas.`)) {
			return;
		}
		this.conduiteService.deleteLibelleItem('Medicaments', m.id).catch(
			e => this.signalerErreur('Suppression du médicament impossible', e),
		);
	}

	// ── Depuis un examen ──────────────────────────────────────────────────────

	private prefillFromExamen(examenId: string) {
		this.conduiteService.getexamensbyid(examenId).pipe(take(1)).subscribe((ex: any) => {
			if (!ex) {
				return;
			}
			this.effacer();
			this.examenOrigine = { id: examenId, date: ex.dateExamen, molecule: ex.conduitemolecule || '' };
			const ferme = ex.entreprise || '';
			this.fermeExamen = ferme;
			this.majFermes();
			this.form.patchValue({
				ferme,
				numCage: ex.numCage || '',
				temperatureEau: ex.tempEau || '',
			});
			this.setLignes([{ duree: ex.conduitedurretraitement || '' }]);
			this.tabIndex = 0;
			this.cdr.markForCheck();
		});
	}

	// ── PDF / partage / Excel ─────────────────────────────────────────────────

	/** Appelé par le menu « avec / sans cachet » puis « avec / sans entité » ; le contexte (quelle ordonnance,
	 * quelle action) vient du bouton cliqué. */
	choisirCachet(avecCachet: boolean, avecEntite: boolean) {
		const ctx = this.menuContext;
		if (!ctx) {
			return;
		}
		const ordonnance = ctx.ordonnance || this.lireFormulaire();
		if (!ordonnance) {
			return;
		}
		if (ctx.action === 'apercu') {
			pdfMakeOrdonnance().then(() => pdfMake.createPdf(buildOrdonnanceDocDef(ordonnance, avecCachet, avecEntite)).open());
		} else if (ctx.action === 'pdf') {
			pdfMakeOrdonnance().then(() =>
				pdfMake.createPdf(buildOrdonnanceDocDef(ordonnance, avecCachet, avecEntite)).download(this.partageService.nomFichier(ordonnance)));
		} else {
			this.ouvrirMenuPartage(ordonnance, avecCachet, avecEntite);
		}
	}

	/**
	 * Ouvre le menu « Partager l'ordonnance » (WhatsApp / Messenger-Email / Autres applications / Imprimer).
	 * Téléphone (≤ 640 px) : feuille du bas standard (MatBottomSheet) — sur une longue liste, notre ancien
	 * panneau `position: fixed` maison pouvait s'afficher loin en dessous de l'écran visible sur iOS Safari,
	 * obligeant à tout faire défiler pour le voir (retour du client du 2026-09-23). MatBottomSheet s'appuie
	 * sur le CDK Overlay, qui gère ce cas correctement.
	 * Ordinateur : menu ancré personnalisé sous le bouton cliqué (inchangé, voir ouvrirMenuPartageAncre()).
	 */
	ouvrirMenuPartage(ordonnance: Ordonnance, avecCachet: boolean, avecEntite: boolean) {
		if (window.innerWidth <= 640) {
			this.menuPartageBouton = null;
			this.bottomSheet.open(PartageOrdonnanceSheetComponent, {
				data: { ordonnance, avecCachet, avecEntite } as PartageOrdonnanceSheetData,
				panelClass: 'partage-sheet-panel',
			});
			return;
		}
		this.ouvrirMenuPartageAncre(ordonnance, avecCachet, avecEntite);
	}

	private ouvrirMenuPartageAncre(ordonnance: Ordonnance, avecCachet: boolean, avecEntite: boolean) {
		const bouton = this.menuPartageBouton;
		// Ce clic ferme aussi le menu « Avec/Sans cachet » qui précède : mesurer tout de suite se ferait
		// pendant que ce premier menu se referme encore (CDK bloque/restaure le défilement de la page à ce
		// moment-là), avec une position faussée. On attend que tout soit retombé.
		setTimeout(() => {
			// 1re passe : affiché hors écran, juste pour mesurer sa VRAIE hauteur (variable selon la longueur
			// des recommandations, du nom de la ferme…) — deviner une hauteur fixe plaçait le panneau loin du
			// bouton dès que l'estimation était fausse.
			this.menuPartagePosition = { top: '-9999px', left: '-9999px' };
			this.menuPartage = { ordonnance, avecCachet, avecEntite };
			this.cdr.detectChanges();
			if (bouton && this.panelPartageEl) {
				const hauteur = this.panelPartageEl.nativeElement.getBoundingClientRect().height;
				this.menuPartagePosition = this.positionAncree(bouton, hauteur);
				this.cdr.markForCheck();
			}
		});
	}

	fermerMenuPartage() {
		this.menuPartage = null;
		this.menuPartagePosition = null;
		this.menuPartageBouton = null;
	}

	/**
	 * Position ancrée sous (ou au-dessus, si la place manque) le bouton « Partager » cliqué — comme le menu
	 * « Avec/Sans cachet » qui le précède. `null` sur petit écran : le panneau reste centré (plus proche de la
	 * maquette mobile du client, et un dropdown collé au bord du doigt serait peu maniable).
	 */
	/** `hauteurPanneau` = hauteur réelle mesurée du panneau (voir ouvrirMenuPartage) : pas d'estimation à deviner. */
	private positionAncree(bouton: HTMLElement, hauteurPanneau: number): { top: string; left: string } {
		if (!bouton || window.innerWidth <= 640) {
			return null;
		}
		const marge = 12;
		const r = bouton.getBoundingClientRect();
		let left = r.right - this.LARGEUR_PANNEAU_ANCRE;
		left = Math.max(marge, Math.min(left, window.innerWidth - this.LARGEUR_PANNEAU_ANCRE - marge));
		let top = r.bottom + 8;
		if (top + hauteurPanneau > window.innerHeight - marge) {
			top = Math.max(marge, r.top - hauteurPanneau - 8);
		}
		return { top: top + 'px', left: left + 'px' };
	}

	// ── Combiner 2 ordonnances sur une page A4 (économie de papier) ────────────

	estSelectionnePourCombinaison(o: Ordonnance): boolean {
		return this.combinaisonSelection.some(x => x.id === o.id);
	}

	toggleCombinaison(o: Ordonnance) {
		const i = this.combinaisonSelection.findIndex(x => x.id === o.id);
		if (i >= 0) {
			this.combinaisonSelection.splice(i, 1);
		} else if (this.combinaisonSelection.length < 2) {
			this.combinaisonSelection.push(o);
		}
	}

	annulerCombinaison() {
		this.combinaisonSelection = [];
	}

	/** Génère le PDF A4 paysage (2 ordonnances A5 côte à côte, cachet identique pour les deux). */
	combiner(avecCachet: boolean) {
		if (this.combinaisonSelection.length !== 2) {
			return;
		}
		const [o1, o2] = this.combinaisonSelection;
		pdfMakeOrdonnance().then(() => pdfMake.createPdf(buildDoubleOrdonnanceDocDef(o1, avecCachet, o2, avecCachet)).open());
		this.annulerCombinaison();
	}

	// Les 4 actions du menu ancré (ordinateur) délèguent à OrdonnancePartageService — la même implémentation
	// que la feuille du bas mobile (PartageOrdonnanceSheetComponent), écrite une seule fois.
	partagerWhatsApp() {
		const { ordonnance, avecCachet, avecEntite } = this.menuPartage;
		this.fermerMenuPartage();
		this.partageService.partagerWhatsApp(ordonnance, avecCachet, avecEntite);
	}

	partagerMessengerEmail() {
		const { ordonnance, avecCachet, avecEntite } = this.menuPartage;
		this.fermerMenuPartage();
		this.partageService.partagerMessengerEmail(ordonnance, avecCachet, avecEntite);
	}

	partagerAutresApplications() {
		const { ordonnance, avecCachet, avecEntite } = this.menuPartage;
		this.fermerMenuPartage();
		this.partageService.partagerAutresApplications(ordonnance, avecCachet, avecEntite);
	}

	partagerImprimerPdf() {
		const { ordonnance, avecCachet, avecEntite } = this.menuPartage;
		this.fermerMenuPartage();
		this.partageService.partagerImprimerPdf(ordonnance, avecCachet, avecEntite);
	}

	exporterExcel() {
		const lignes = this.ordonnancesFiltrees.map(o => ({
			Date: o.dateOrdonnance,
			Ferme: o.ferme,
			Cage: o.numCage,
			'Température eau (°C)': o.temperatureEau,
			Médicaments: lignesDe(o).map(l => `${l.medicament} — ${l.dose} — ${l.duree}`).join('\n'),
			'Voie d\'administration': o.voieAdministration,
			Recommandations: o.recommandations,
		}));
		const classeur = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(classeur, XLSX.utils.json_to_sheet(lignes), 'Ordonnances');
		XLSX.writeFile(classeur, 'Ordonnances.xlsx');
	}

	// ── Utilitaires ───────────────────────────────────────────────────────────

	private majFermes() {
		const extra = this.fermeExamen && this.fermesFirestore.indexOf(this.fermeExamen) === -1 ? [this.fermeExamen] : [];
		this.fermes = [...this.fermesFirestore, ...extra];
	}

	/** Message d'erreur qui reste affiché : « permission-denied » = règles Firestore pas encore déployées. */
	private signalerErreur(contexte: string, e: any) {
		console.error(contexte, e);
		const cause = e && e.code === 'permission-denied'
			? 'accès refusé par Firestore — les règles (firestore.rules) ne sont probablement pas déployées'
			: (e && e.message) || 'erreur inconnue';
		this.snackBar.open(`${contexte} : ${cause}`, 'OK', { duration: 15000 });
	}

	private libelles(actions: any[]): string[] {
		return actions
			.map(a => String(a.payload.doc.data().libelle || '').trim())
			.filter(l => !!l)
			.sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
	}

	private today(): string {
		const d = new Date();
		return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
